import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  Globe,
  Info,
  Image as ImageIcon,
  Calendar,
  LogOut,
  Bell,
  X,
  ClipboardList,
} from 'lucide-react';
import CookieBanner from '../components/cookies/CookieBanner';
import { useAdminNotifications } from '../hooks/useAdminNotifications';


const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const { notifications, unreadCount, markAllRead, clearNotifications } = useAdminNotifications();

  // Show a toast whenever a new notification arrives
  const prevCountRef = useRef(0);
  useEffect(() => {
    if (notifications.length > 0 && notifications.length > prevCountRef.current) {
      const latest = notifications[0];
      toast.info(
        <div
          className="cursor-pointer"
          onClick={() => {
            navigate('/admin/adhesions', { state: { refresh: Date.now() } });
            toast.dismiss();
          }}
        >
          <p className="font-semibold text-sm">🔔 Nouvelle demande d'adhésion</p>
          {latest.prenom && latest.nom && (
            <p className="text-xs text-gray-600 mt-0.5">{latest.prenom} {latest.nom}</p>
          )}
          <p className="text-xs text-blue-600 mt-1">Cliquez pour voir →</p>
        </div>,
        { autoClose: 8000, position: 'top-right' }
      );
    }
    prevCountRef.current = notifications.length;
  }, [notifications, navigate]);

  // Close notif dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActiveRoute = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/dashboard';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const menuItems = [
    {
      category: 'Principal',
      items: [
        {
          name: 'Tableau de bord',
          path: '/admin/dashboard',
          icon: LayoutDashboard,
        },
        {
          name: 'Voir le site',
          path: '/',
          icon: Globe,
          external: true,
        },
      ],
    },

    {
      category: 'Publication',
      items: [
        {
          name: 'Contenu',
          path: '/admin/homepage-content',
          icon: FileText,
        },
        {
          name: 'Présentation',
          path: '/admin/presentation',
          icon: Info,
        },
        {
          name: 'Galerie',
          path: '/admin/albums',
          icon: ImageIcon,
        },
        {
          name: 'Événements',
          path: '/admin/evenements',
          icon: Calendar,
        },
      ],
    },
    {
      category: 'Gestion d\'adherants',
      items: [
        {
          name: 'Liste des Adhésions',
          path: '/admin/adhesions',
          icon: Users,
        },
      ],
    },

    {
      category: 'Configuration',
      items: [
        {
          name: 'Info Générales',
          path: '/admin/info',
          icon: Info,
        },
        // {
        //   name: 'Paramètres',
        //   path: '/admin/settings',
        //   icon: Settings,
        // },
      ],
    },
    // {
    //   category: 'Gestion des Utilisateurs',
    //   items: [
    //     {
    //       name: 'Utilisateurs',
    //       path: '/admin/users',
    //       icon: Users,
    //     },
    //     {
    //       name: 'Rôles',
    //       path: '/admin/roles',
    //       icon: UserCircle,
    //     },
    //     {
    //       name: 'Permissions',
    //       path: '/admin/permissions',
    //       icon: Settings,
    //     },
    //   ],
    // },
  ];

  const SidebarContent = () => (
    <>
      {/* Logo & Brand */}
      <div className="p-4 border-b border-gray-30">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-md">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          {!sidebarCollapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <h1 className="text-base font-semibold text-gray-90">Admin</h1>
              <p className="text-xs text-gray-70">La Sapi plateforme</p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        <nav className="px-3 space-y-6">
          {menuItems.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              {!sidebarCollapsed && (
                <motion.h3
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="px-3 text-xs font-semibold text-gray-70 uppercase tracking-wider mb-2"
                >
                  {section.category}
                </motion.h3>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const isActive = isActiveRoute(item.path);
                  const Icon = item.icon;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-gray-70 hover:bg-gray-10 hover:text-primary'
                        }`}
                    >
                      <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-70 group-hover:text-primary'}`} />
                      {!sidebarCollapsed && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="ml-3 truncate"
                        >
                          {item.name}
                        </motion.span>
                      )}
                      {isActive && !sidebarCollapsed && (
                        <motion.div
                          layoutId="activeIndicator"
                          className="ml-auto w-1.5 h-1.5 bg-white rounded-full"
                        />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Logout Section */}
      <div className="p-4 border-t border-gray-30 bg-surface">
        <button
          onClick={() => {
            // Clear auth data
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            // Redirect to login
            navigate('/login');
          }}
          className={`flex items-center w-full px-3 py-2.5 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-all duration-200 group ${sidebarCollapsed ? 'justify-center' : ''
            }`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
          {!sidebarCollapsed && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className="ml-3 truncate"
            >
              Déconnexion
            </motion.span>
          )}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Mobile menu backdrop */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          width: sidebarCollapsed ? '80px' : '256px',
        }}
        className="hidden lg:flex bg-surface border-r border-gray-30 shadow-sm relative z-30"
      >
        <div className="flex flex-col w-full h-full">
          <SidebarContent />
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-20 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center shadow-md hover:bg-primary-hover transition-colors"
          >
            <motion.div
              animate={{ rotate: sidebarCollapsed ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              ←
            </motion.div>
          </button>
        </div>
      </motion.aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-y-0 left-0 z-50 w-64 bg-surface shadow-2xl lg:hidden"
          >
            <div className="flex flex-col h-full">
              <SidebarContent />
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header Bar with Notifications */}
        <header className="h-14 bg-white border-b border-gray-100 flex items-center justify-end px-6 flex-shrink-0">
          {/* Notification Bell */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => {
                setNotifOpen(prev => !prev);
                if (!notifOpen) markAllRead();
              }}
              className="relative p-2 rounded-full hover:bg-gray-50 transition-colors"
              title="Notifications"
            >
              <Bell className="w-5 h-5 text-gray-500" />
              {unreadCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </motion.span>
              )}
            </button>

            {/* Notification Dropdown */}
            <AnimatePresence>
              {notifOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50"
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                    <span className="font-semibold text-sm text-gray-800">Notifications</span>
                    {notifications.length > 0 && (
                      <button
                        onClick={clearNotifications}
                        className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        Tout effacer
                      </button>
                    )}
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="py-10 text-center text-gray-400">
                        <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Aucune notification</p>
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <button
                          key={notif.id}
                          onClick={() => {
                            navigate('/admin/adhesions', { state: { refresh: Date.now() } });
                            setNotifOpen(false);
                          }}
                          className="w-full flex items-start gap-3 px-4 py-3 hover:bg-blue-50/50 text-left transition-colors group border-b border-gray-50 last:border-0"
                        >
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                            <ClipboardList className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 leading-tight">{notif.message}</p>
                            {notif.prenom && notif.nom && (
                              <p className="text-xs text-gray-500 mt-0.5">{notif.prenom} {notif.nom}</p>
                            )}
                            <p className="text-[10px] text-gray-400 mt-1">
                              {new Date(notif.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>

                  {notifications.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-50">
                      <button
                        onClick={() => { navigate('/admin/adhesions', { state: { refresh: Date.now() } }); setNotifOpen(false); }}
                        className="w-full text-center text-sm text-primary font-medium py-1.5 hover:text-primary-dark transition-colors"
                      >
                        Voir toutes les demandes →
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-10 p-4 lg:p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      <CookieBanner />
    </div>
  );
};

export default AdminLayout;
